import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import {
  RisksType,
  ContractRenewalStatusDto,
  RISK_STATUS,
  RISK_TYPE,
  useNewProjectService,
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { ASSUMPTION_STATUS, AssumptionType } from '../../assumptions/types';
import AssumptionSection from './sections/AssumptionSection';
import RiskSection from './sections/RiskSection';
import { ControlData } from '../types';
import { EMPTY_MESSAGES, SECTION_TITLES } from './constants';
import RequiredActionsSection from './sections/RequiredActionsSection';
import ContractRenewalConfirmModal from '../shared/ContractRenewalConfirmModal';
import {
  getCommercialRequiredActionsHealthchecks,
  getContractRenewalMessage,
  hasCommercialContractRenewalIssue,
  shouldShowCommercialRequiredActions,
} from './utils';
import { getOutdatedRisks } from '../utils';
import { useCommercialRequiredActions } from './requiredActions';

type CommercialControlProps = {
  data: ControlData;
  refetchControlsData: () => Promise<unknown>;
};

const CommercialSections = ({ data, refetchControlsData }: CommercialControlProps) => {
  const { addMessage } = useMessageService();
  const { getContractRenewalStatus, patchContractRenewal } = useNewProjectService();
  const { isAccessGranted } = useProjectSecurityContext();
  const allRisks = data?.risks || [];
  const allAssumptions = data?.assumptions || [];
  const [renewalStatus, setRenewalStatus] = useState<ContractRenewalStatusDto | null>(null);
  const [isRenewalLoading, setIsRenewalLoading] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);

  const activeRisks = allRisks.filter(
    (risk: RisksType) => risk.type === RISK_TYPE.COMMERCIAL && risk.status === RISK_STATUS.ACTIVE,
  );

  const escalatedRisks = activeRisks.filter((risk: RisksType) => risk.isEscalated);
  const otherRisks = activeRisks.filter((risk: RisksType) => !risk.isEscalated);
  const outdatedRisks = getOutdatedRisks(activeRisks);

  const violatedAssumptions = allAssumptions.filter(
    (assumption: AssumptionType) => assumption?.status === ASSUMPTION_STATUS.VIOLATED,
  );

  const requiredActionHealthchecks = useMemo(() => getCommercialRequiredActionsHealthchecks(data), [data]);
  const canViewContractRenewal = isAccessGranted(ACCESS_INFO_SECTIONS.CONTRACT_RENEWAL, ACCESS_INFO_ACTIONS.VIEW);
  const hasContractRenewalIssue =
    canViewContractRenewal && hasCommercialContractRenewalIssue(requiredActionHealthchecks);
  const renewalRequestKey =
    hasContractRenewalIssue && data?.accountData?.accountId
      ? `${data.accountData.accountId}:${data.legacyId ?? ''}`
      : null;

  useEffect(() => {
    if (!renewalRequestKey) {
      setRenewalStatus(null);
      setIsRenewalLoading(false);
      return;
    }

    const accountId = data.accountData?.accountId;
    if (!accountId) {
      return;
    }

    setIsRenewalLoading(true);

    getContractRenewalStatus(String(accountId), data.legacyId)
      .then(setRenewalStatus)
      .catch(() => addMessage(new ErrorMessage('Error fetching contract renewal status.')))
      .finally(() => setIsRenewalLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renewalRequestKey]);

  const contractRenewalMessage = useMemo(
    () => getContractRenewalMessage(renewalStatus, requiredActionHealthchecks.contractRenewalHealthcheck),
    [renewalStatus, requiredActionHealthchecks.contractRenewalHealthcheck],
  );

  const requiredActions = useCommercialRequiredActions({
    data,
    refetchControlsData,
    renewalStatus,
    isRenewalLoading,
    contractRenewalMessage,
    onContractRenewalConfirm: () => setConfirmModalOpen(true),
  });

  const shouldShowRequiredActions = shouldShowCommercialRequiredActions({
    actionsCount: requiredActions.length,
    healthchecks: requiredActionHealthchecks,
  });

  return (
    <Box sx={{ px: 2, py: 1 }}>
      {shouldShowRequiredActions && (
        <RequiredActionsSection title={SECTION_TITLES.REQUIRED_ACTIONS} actions={requiredActions} />
      )}
      <RiskSection
        title={SECTION_TITLES.ESCALATED_COMMERCIAL_RISKS}
        risks={escalatedRisks}
        emptyMessage={EMPTY_MESSAGES.ESCALATED_RISKS}
      />
      <RiskSection
        title={SECTION_TITLES.OTHER_COMMERCIAL_RISKS}
        risks={otherRisks}
        emptyMessage={EMPTY_MESSAGES.OTHER_RISKS}
      />
      {outdatedRisks.length > 0 && (
        <RiskSection
          title={SECTION_TITLES.OUTDATED_RISKS}
          risks={outdatedRisks}
          emptyMessage={EMPTY_MESSAGES.OTHER_RISKS}
        />
      )}
      <AssumptionSection
        title={SECTION_TITLES.VIOLATED_ASSUMPTIONS}
        assumptions={violatedAssumptions}
        emptyMessage={EMPTY_MESSAGES.VIOLATED_ASSUMPTIONS}
      />
      <ContractRenewalConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        endDate={data?.budget?.endDate}
        onConfirm={async () => {
          await patchContractRenewal(data.id, {
            isContractRenewalRequired: false,
          });
          await refetchControlsData();
          setConfirmModalOpen(false);
        }}
      />
    </Box>
  );
};

export default CommercialSections;
